/*
This is a plugin for Stimulus controllers to add standard behaviour for reacting
to clicks outside the component.

In the default two-phase mode most of the complexity comes from supporting 2
corner case scenarios:
1. The user clicks down inside the component and drags the mouse out before releasing.
2. The user clicks down outside the component and drags the mouse inside.
Both should not close the component. If users are doing this then it's likely they are
selecting something in the component.

The very basic usage is to use all of the defaults and just close the component
on outer click would look like this:
```
HB.useOuterClick(this, { onClose: () => this.close() })
```

Single-event mode: pass `triggerEvents` (e.g. `["click"]`) to skip the
start/finish arming entirely and fire `onClose` directly on each trigger event,
gated by `isOuterEvent` then `shouldFinish`. `startEvents`/`finishEvents`/
`shouldStart` are ignored in this mode.

`useCapture: true` attaches every listener in the capture phase — required when
a delegated handler upstream calls `stopPropagation()` before the bubble reaches
the `listenerTarget`.

See the code below, and specifically the `defaults` constant for other options.
*/
;(() => {
  const outerClickState = Symbol.for("HB.useOuterClick.state")

  const defaults = {
    startEvents: ["mousedown", "touchstart"],
    finishEvents: ["mouseup", "touchend"],
    triggerEvents: null,
    useCapture: false,
    shouldStart: () => true,
    shouldFinish: () => true,
    preventDefault: false,
    onClose: () => {},
  }

  const shouldPreventDefault = (option, event) => {
    if (typeof option === "function") return option(event)
    return Boolean(option)
  }

  ;(window.HB = window.HB || {}).useOuterClick = function useOuterClick(
    controller,
    options = {},
  ) {
    const config = {
      ...defaults,
      listenerTarget: document,
      isOuterEvent: (event) => !controller.element.contains(event.target),
      ...options,
    }

    const singleEvent = Boolean(config.triggerEvents)

    let observing = false
    let finishObserving = false

    const removeFinishListeners = () => {
      if (!finishObserving) return
      config.finishEvents.forEach((eventName) =>
        config.listenerTarget.removeEventListener(
          eventName,
          finish,
          config.useCapture,
        ),
      )
      finishObserving = false
    }

    const addFinishListeners = () => {
      removeFinishListeners()
      config.finishEvents.forEach((eventName) =>
        config.listenerTarget.addEventListener(
          eventName,
          finish,
          config.useCapture,
        ),
      )
      finishObserving = true
    }

    const close = (event) => {
      if (!config.isOuterEvent(event)) return
      if (!config.shouldFinish(event)) return
      if (shouldPreventDefault(config.preventDefault, event)) {
        event.preventDefault()
      }
      config.onClose(event)
    }

    const finish = (event) => {
      removeFinishListeners()
      close(event)
    }

    const start = (event) => {
      removeFinishListeners()
      if (!config.shouldStart(event)) return
      if (!config.isOuterEvent(event)) return
      addFinishListeners()
    }

    // Single-event mode: no arming — run the same `close` pipeline directly on
    // each trigger event. Default mode arms via `start` and defers to `finish`.
    const observeEvents = singleEvent
      ? config.triggerEvents
      : config.startEvents
    const observeHandler = singleEvent ? close : start

    const toggleObserveListeners = (method) =>
      observeEvents.forEach((eventName) =>
        config.listenerTarget[method](
          eventName,
          observeHandler,
          config.useCapture,
        ),
      )

    const observe = () => {
      if (observing) return
      toggleObserveListeners("addEventListener")
      observing = true
    }

    const unobserve = () => {
      if (observing) {
        toggleObserveListeners("removeEventListener")
        observing = false
      }
      removeFinishListeners()
    }

    const state = controller[outerClickState]
    state?.unobserve()

    const originalDisconnect =
      state?.originalDisconnect ||
      controller.disconnect?.bind(controller) ||
      (() => {})

    controller[outerClickState] = { unobserve, originalDisconnect }

    if (!state) {
      Object.assign(controller, {
        disconnect() {
          controller[outerClickState]?.unobserve()
          originalDisconnect()
        },
      })
    }

    observe()
    return [observe, unobserve]
  }
})()
